import React, { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import {
  PhotoIcon,
  PaperAirplaneIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import postService, {
  BroadcastButton,
  BroadcastRoomOption,
} from '../api/services/post.service';

const MAX_BUTTONS = 3;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5 MB

interface LocalImage {
  dataUrl: string;
  filename: string;
  size: number;
  mime: string;
}

const Post: React.FC = () => {
  const [text, setText] = useState('');
  const [image, setImage] = useState<LocalImage | null>(null);
  const [buttons, setButtons] = useState<BroadcastButton[]>([]);

  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState<{
    type: 'success' | 'error';
    msg: string;
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  /* =========================================================
     BROADCAST ROOMS QUERY
     - Rooms change rarely → cache for 5 minutes
     - refetchOnMount: false → cached list renders instantly
       when the admin navigates back to the Post page
     - refetchOnWindowFocus: false → no refetch on tab switch
  ========================================================= */
  const {
    data: rooms,
    isLoading: roomsLoading,
    error: roomsError,
  } = useQuery({
    queryKey: ['broadcast-rooms'],
    queryFn: () => postService.getRooms(),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const roomOptions: BroadcastRoomOption[] = rooms || [];

  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('error', 'Please select an image file');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      showToast('error', 'Image must be under 5 MB');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') return;
      setImage({
        dataUrl: result,
        filename: file.name,
        size: file.size,
        mime: file.type,
      });
    };
    reader.onerror = () => {
      showToast('error', 'Failed to read image');
    };
    reader.readAsDataURL(file);

    // Reset so selecting the same file again re-triggers change
    e.target.value = '';
  };

  const removeImage = () => setImage(null);

  const addButton = () => {
    if (buttons.length >= MAX_BUTTONS) return;
    setButtons((prev) => [
      ...prev,
      { label: '', roomId: roomOptions[0]?.roomId || '' },
    ]);
  };

  const updateButton = (i: number, patch: Partial<BroadcastButton>) => {
    setButtons((prev) =>
      prev.map((b, idx) => (idx === i ? { ...b, ...patch } : b))
    );
  };

  const removeButton = (i: number) => {
    setButtons((prev) => prev.filter((_, idx) => idx !== i));
  };

  const canSend = useMemo(() => {
    const hasText = text.trim().length > 0;
    const hasImage = !!image;
    if (!hasText && !hasImage) return false;
    for (const b of buttons) {
      if (!b.label.trim() || !b.roomId.trim()) return false;
    }
    return true;
  }, [text, image, buttons]);

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const result = await postService.sendBroadcast({
        text: text.trim(),
        image: image
          ? { dataUrl: image.dataUrl, filename: image.filename }
          : undefined,
        buttons: buttons.map((b) => ({
          label: b.label.trim(),
          roomId: b.roomId,
        })),
      });

      showToast(
        'success',
        `Broadcast sent · ${result.sent} delivered, ${result.failed} failed`
      );

      setText('');
      setImage(null);
      setButtons([]);
    } catch (e: any) {
      showToast(
        'error',
        e?.response?.data?.message || e?.message || 'Failed to send broadcast'
      );
    } finally {
      setSending(false);
    }
  };

  const formatBytes = (n: number) =>
    n < 1024
      ? `${n} B`
      : n < 1024 * 1024
      ? `${(n / 1024).toFixed(1)} KB`
      : `${(n / (1024 * 1024)).toFixed(2)} MB`;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Post</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Broadcast a message to all users on the Telegram bot.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left column — form */}
        <div className="lg:col-span-3 space-y-4">
          {/* Message */}
          <Card>
            <h2 className="text-sm font-semibold text-gray-900 mb-4">
              Message
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Text
                </label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={5}
                  placeholder="Write your broadcast message..."
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  HTML supported: &lt;b&gt;bold&lt;/b&gt;, &lt;i&gt;italic&lt;/i&gt;,
                  &lt;a href="..."&gt;link&lt;/a&gt;
                </p>
              </div>

              {/* Image upload */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Image (optional)
                </label>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {!image ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full border-2 border-dashed border-gray-200 hover:border-primary-300 hover:bg-primary-50/30 rounded-lg px-4 py-6 flex flex-col items-center gap-2 transition-colors"
                  >
                    <PhotoIcon className="w-7 h-7 text-gray-400" />
                    <span className="text-xs font-medium text-gray-600">
                      Click to upload an image
                    </span>
                    <span className="text-[10px] text-gray-400">
                      PNG, JPG, WEBP · max 5 MB
                    </span>
                  </button>
                ) : (
                  <div className="border border-gray-200 rounded-lg p-2 flex items-center gap-3">
                    <img
                      src={image.dataUrl}
                      alt="preview"
                      className="w-16 h-16 rounded-md object-cover flex-shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-900 truncate">
                        {image.filename}
                      </p>
                      <p className="text-[10px] text-gray-500">
                        {formatBytes(image.size)} · {image.mime}
                      </p>
                    </div>
                    <button
                      onClick={removeImage}
                      className="p-2 rounded-lg hover:bg-red-50 text-red-500 flex-shrink-0"
                      title="Remove image"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Buttons */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">
                  Buttons (optional)
                </h2>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  Up to {MAX_BUTTONS} · each opens a game room in the mini app
                </p>
              </div>
              <button
                onClick={addButton}
                disabled={
                  buttons.length >= MAX_BUTTONS ||
                  roomsLoading ||
                  !!roomsError ||
                  roomOptions.length === 0
                }
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-40"
              >
                <PlusIcon className="w-3.5 h-3.5" />
                Add Button
              </button>
            </div>

            {roomsLoading && (
              <p className="text-xs text-gray-500">Loading rooms...</p>
            )}

            {roomsError && (
              <p className="text-xs text-red-500">
                Failed to load rooms. Reload the page.
              </p>
            )}

            {!roomsLoading && !roomsError && roomOptions.length === 0 && (
              <p className="text-xs text-gray-500">
                No game rooms exist yet — create one first.
              </p>
            )}

            {!roomsLoading &&
              roomOptions.length > 0 &&
              buttons.length === 0 && (
                <p className="text-xs text-gray-500">
                  No buttons added. The broadcast will send as plain text/image.
                </p>
              )}

            <div className="space-y-2 mt-2">
              {buttons.map((b, i) => (
                <div
                  key={i}
                  className="flex flex-col md:flex-row md:items-center gap-2 p-2 border border-gray-100 rounded-lg"
                >
                  <span className="text-xs font-medium text-gray-400 w-6">
                    #{i + 1}
                  </span>
                  <input
                    type="text"
                    placeholder="Button label (e.g. 🎯 Play room-10)"
                    value={b.label}
                    onChange={(e) =>
                      updateButton(i, { label: e.target.value })
                    }
                    className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                  <select
                    value={b.roomId}
                    onChange={(e) =>
                      updateButton(i, { roomId: e.target.value })
                    }
                    className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  >
                    {roomOptions.map((r) => (
                      <option key={r.roomId} value={r.roomId}>
                        {r.roomId} · {r.betAmount} ETB
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => removeButton(i)}
                    className="p-2 rounded-lg hover:bg-red-50 text-red-500 flex-shrink-0"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </Card>

          <div className="flex justify-end">
            <Button
              size="md"
              onClick={handleSend}
              disabled={!canSend || sending}
            >
              <PaperAirplaneIcon className="w-4 h-4 mr-1.5" />
              {sending ? 'Sending...' : 'Send Broadcast'}
            </Button>
          </div>
        </div>

        {/* Right column — preview */}
        <div className="lg:col-span-2">
          <Card>
            <h2 className="text-sm font-semibold text-gray-900 mb-4">
              Preview
            </h2>

            <div className="bg-[#eef1f5] rounded-xl p-3">
              <div className="bg-white rounded-2xl shadow-sm p-3 max-w-sm mx-auto">
                {image && (
                  <img
                    src={image.dataUrl}
                    alt="preview"
                    className="w-full rounded-lg mb-2 object-cover max-h-64"
                  />
                )}
                <div
                  className="text-sm text-gray-900 whitespace-pre-wrap break-words"
                  dangerouslySetInnerHTML={{
                    __html: text || '<span class="text-gray-400">No text</span>',
                  }}
                />
                {buttons.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {buttons.map((b, i) => (
                      <div
                        key={i}
                        className="text-center text-xs font-medium text-blue-600 bg-blue-50 rounded-md py-1.5"
                      >
                        {b.label || 'Button'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <p className="text-[10px] text-gray-400 mt-3 text-center">
              Preview approximates Telegram rendering
            </p>
          </Card>
        </div>
      </div>

      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
            toast.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default Post;